import { Edit, PersonOutline, Share } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Modal,
  Paper,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { format, parseISO } from "date-fns";
import { startCase } from "lodash";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { patientApi } from "../../api/patientApi";
import { useDebouncedEffect } from "../../hooks/useDebouncedEffect";
import DotsVertical from "../../icons/DotsVertical";
import SearchIcon from "../../icons/Search";
import { useOrganizationReference } from "../../pages/facility/hooks/useOrganization";
import { generateHumanDisplayName } from "../../utils/DataTransformationUtils";
import getInitials from "../../utils/getInitials";
import { Patient } from "../../types/patient";
import {
  parseSearchTokens,
  wordPrefixPattern,
} from "../../utils/optionSearchMatch";
import ShortcutDisplay from "../shortcuts/ShortcutDisplay";
import { NotificationVariant, useNotifications } from "./NotificationsProvider";

/** Shorter queries match too much of the directory to be worth a round trip. */
const MIN_SEARCH_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 250;

/**
 * `birthDate` is declared as a Date on the Patient type but arrives from the
 * API as an ISO string. `parseISO` is used rather than `new Date` so date-only
 * values resolve in local time and cannot render a day early.
 */
const formatBirthDate = (birthDate: Patient["birthDate"]) =>
  format(parseISO(birthDate as unknown as string), "dd/MMM/yyyy");

const TELECOM_PRIORITY = ["phone", "mobile", "sms", "email"];

/**
 * One contact to show in the row menu, plus how many other unique values
 * exist. Phone is preferred over email because staff typically call first.
 */
const getTelecomPreview = (telecom: Patient["telecom"]) => {
  const values = (telecom ?? [])
    .map((entry) => ({
      system: entry?.system?.toLowerCase() ?? "",
      value: entry?.value?.trim() ?? "",
    }))
    .filter((entry) => entry.value.length > 0);

  const unique = values.filter(
    (entry, index, all) =>
      all.findIndex((other) => other.value === entry.value) === index,
  );

  unique.sort((a, b) => {
    const aRank = TELECOM_PRIORITY.indexOf(a.system);
    const bRank = TELECOM_PRIORITY.indexOf(b.system);
    return (aRank === -1 ? 99 : aRank) - (bRank === -1 ? 99 : bRank);
  });

  return {
    primary: unique[0]?.value,
    extraCount: Math.max(unique.length - 1, 0),
  };
};

const KeyHint = ({ children }: { children: React.ReactNode }) => (
  <Box
    component="kbd"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      minWidth: 22,
      height: 20,
      px: 0.75,
      borderRadius: 0.75,
      border: 1,
      borderColor: "divider",
      bgcolor: "action.hover",
      fontSize: "0.7rem",
      fontWeight: 600,
      lineHeight: 1,
      color: "text.secondary",
      fontFamily: "inherit",
    }}
  >
    {children}
  </Box>
);

const KeyboardLegendItem = ({
  keys,
  label,
}: {
  keys: React.ReactNode;
  label: string;
}) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
    {keys}
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
  </Box>
);

const ContentSearch: FC = () => {
  const theme = useTheme();
  const [value, setValue] = useState<string>("");
  const [open, setOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showResults, setShowResults] = useState<boolean>(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const [isMouseOver, setIsMouseOver] = useState<boolean>(false);
  const [results, setResults] = useState({ patients: [] });
  const [patientName, setPatientName] = useState<Record<string, string>>({});
  const [rowMenuAnchor, setRowMenuAnchor] = useState<HTMLElement | null>(null);
  const [rowMenuPatient, setRowMenuPatient] = useState<Patient | null>(null);
  const organizationReference = useOrganizationReference();
  const { setNotification } = useNotifications();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const latestRequestRef = useRef<number>(0);
  // Read by the window-level Escape handler, which is registered once and so
  // cannot see current state.
  const rowMenuOpenRef = useRef<boolean>(false);
  const resultRefs = useRef<React.MutableRefObject<(HTMLDivElement | null)[]>>({
    current: [],
  });

  useEffect(() => {
    if (!open) {
      // Retire any in-flight request so a late response cannot repopulate the
      // panel the next time the modal is opened.
      latestRequestRef.current += 1;
      setValue("");
      setResults({ patients: [] });
      setShowResults(false);
      setIsLoading(false);
      setHighlightedIndex(-1);
      // The menu is anchored to a row that is about to unmount.
      rowMenuOpenRef.current = false;
      setRowMenuAnchor(null);
      setRowMenuPatient(null);
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 50); // give the modal time to render
    }
  }, [open]);

  useEffect(() => {
    const trimmed = value.trim().split(" ");
    const name =
      trimmed.length > 2
        ? {
            firstName: trimmed[0],
            middleName: trimmed[1],
            family: trimmed[2],
          }
        : {
            firstName: trimmed[0],
            family: trimmed[1],
          };
    setPatientName(name);
  }, [value]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setShowResults(false);
    setIsLoading(false);
    setValue("");
  };

  useEffect(() => {
    const handleShortcut = (e: globalThis.KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen(true);
      }
      if (e.key === "Escape") {
        // Let an open row menu take the first Escape and close only itself.
        if (rowMenuOpenRef.current) return;
        setOpen(false);
      }
    };

    window.addEventListener("keydown", handleShortcut);
    return () => window.removeEventListener("keydown", handleShortcut);
  }, []);

  const organizationId = organizationReference?.identifier;

  const search = useCallback(
    async (query: string) => {
      if (!organizationId) return;

      latestRequestRef.current += 1;
      const requestId = latestRequestRef.current;
      setIsLoading(true);
      try {
        const patients = await patientApi.findPatientsByOrg({
          "managing-organization": organizationId,
          q: query,
        });
        // Keystrokes can resolve out of order, so a response is only applied
        // while it is still the most recent one requested.
        if (requestId !== latestRequestRef.current) return;
        setResults({ patients });
        setHighlightedIndex(-1);
        setShowResults(true);
      } catch (e) {
        if (requestId !== latestRequestRef.current) return;
        console.error("Search error", e);
        setResults({ patients: [] });
        setShowResults(true);
      } finally {
        if (requestId === latestRequestRef.current) setIsLoading(false);
      }
    },
    [organizationId],
  );

  useDebouncedEffect(
    () => {
      if (!open) return;

      const query = value.trim();
      if (query.length < MIN_SEARCH_LENGTH) {
        // Retire any in-flight request so it cannot repopulate a cleared list.
        latestRequestRef.current += 1;
        setResults({ patients: [] });
        setShowResults(false);
        setIsLoading(false);
        return;
      }

      search(query);
    },
    [value, open, search],
    SEARCH_DEBOUNCE_MS,
  );

  const highlightMatch = (text: string, query: string) => {
    const tokens = parseSearchTokens(query);
    if (tokens.length === 0) return text;

    const pattern = tokens.map(wordPrefixPattern).join("|");
    // Splitting on a capturing group leaves the matched text at every odd index,
    // which avoids testing a stateful global regex against each part in turn.
    const parts = text.split(new RegExp(`(${pattern})`, "i"));

    let offset = 0;
    return parts.map((part, index) => {
      // The offset into the name is unique and stable across re-renders, which
      // the position in the array is not.
      const key = `${offset}:${part}`;
      offset += part.length;

      return index % 2 === 1 ? (
        <Typography
          key={key}
          component="span"
          sx={{
            fontWeight: 600,
            bgcolor:
              theme.palette.mode === "light" ? "#fcefdc" : "action.selected",
            borderRadius: 1,
            p: 0.05,
          }}
        >
          {part}
        </Typography>
      ) : (
        <span key={key}>{part}</span>
      );
    });
  };

  const handleKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.code === "ArrowDown") {
      event.preventDefault();
      setHighlightedIndex((prev) =>
        prev < results.patients.length - 1 ? prev + 1 : prev,
      );
    } else if (event.code === "ArrowUp") {
      event.preventDefault();
      setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev));
    } else if (event.code === "Enter") {
      // Results arrive as the user types, so Enter only opens the selection.
      const selectedPatient = results.patients[highlightedIndex];
      if (selectedPatient) {
        navigate(`/patients/${selectedPatient.id}`);
        handleClose();
      }
    }
  };

  useEffect(() => {
    if (
      highlightedIndex !== null &&
      highlightedIndex >= 0 &&
      resultRefs.current[highlightedIndex]
    ) {
      resultRefs.current[highlightedIndex]?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [highlightedIndex]);

  const createNewPatient = async () => {
    navigate("/patients/new", { state: { name: [patientName] } });
    handleClose();
  };

  const openRowMenu = (
    event: React.MouseEvent<HTMLElement>,
    patient: Patient,
  ) => {
    // The row is a link, so the trigger must not navigate or select.
    event.preventDefault();
    event.stopPropagation();
    rowMenuOpenRef.current = true;
    setRowMenuAnchor(event.currentTarget);
    setRowMenuPatient(patient);
  };

  const closeRowMenu = () => {
    rowMenuOpenRef.current = false;
    setRowMenuAnchor(null);
    setRowMenuPatient(null);
  };

  const goToPatient = (path: string) => {
    navigate(path);
    closeRowMenu();
    handleClose();
  };

  const sharePatient = async (patientId: string) => {
    const link = `${window.location.origin}/patients/${patientId}`;
    closeRowMenu();
    try {
      await navigator.clipboard.writeText(link);
      setNotification({
        message: "Patient profile link copied to clipboard.",
        variant: NotificationVariant.success,
      });
    } catch {
      setNotification({
        message: "Could not copy the patient profile link.",
        variant: NotificationVariant.error,
      });
    }
  };

  // Only the very first search shows a spinner in place of the list; later
  // refinements keep the previous results on screen to avoid flashing on
  // every keystroke.
  const isInitialLoading = isLoading && results.patients.length === 0;
  const isPanelVisible = isInitialLoading || showResults;
  const rowMenuTelecom = getTelecomPreview(rowMenuPatient?.telecom);

  return (
    <>
      <Tooltip title="Search">
        <IconButton color="inherit" onClick={handleOpen}>
          <SearchIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Modal
        open={open}
        onClose={handleClose}
        disableEnforceFocus
        disableAutoFocus
        aria-labelledby="search-modal"
      >
        <Box
          onClick={handleClose}
          sx={{
            display: "flex",
            alignItems: "start",
            justifyContent: "center",
            height: "100vh",
            px: { xs: 1.5, sm: 3 },
            py: 10,
          }}
        >
          <Paper
            onClick={(e) => e.stopPropagation()}
            elevation={0}
            sx={{
              width: "100%",
              maxWidth: 700,
              borderRadius: 3,
              bgcolor: "Background.paper",
              px: 2,
              pt: 2,
              pb: 0,
              boxShadow: `
                   0px 8px 16px rgba(0, 0, 0, 0.04),
                   0px 16px 32px rgba(0, 0, 0, 0.06),
                   0px 24px 48px rgba(0, 0, 0, 0.08)
                 `,
              transition: "all 0.3s ease",
            }}
          >
            <Box sx={{ display: "flex" }}>
              <TextField
                fullWidth
                placeholder="Search for a patient by name, ID, or date of birth..."
                value={value}
                onChange={(e) => {
                  setValue(e.target.value);
                  setHighlightedIndex(-1);
                }}
                onKeyUp={handleKeyUp}
                inputRef={inputRef}
                variant="outlined"
                InputLabelProps={{ shrink: false }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon
                        fontSize="small"
                        sx={{ color: "text.disabled" }}
                      />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end" sx={{ gap: 1 }}>
                      {isLoading && <CircularProgress size={18} />}
                      <ShortcutDisplay>Ctrl/⌘ + K</ShortcutDisplay>
                    </InputAdornment>
                  ),
                  sx: {
                    px: 2,
                    py: 1.5,
                    fontSize: "1.05rem",
                    height: 52,
                    borderRadius: 2,
                    bgcolor: "Background.textfield",
                    boxShadow: "none",
                    border: "1px solid transparent",
                    "&:hover": {
                      borderColor: "rgba(0, 0, 0, 0.05)",
                    },
                    "&.Mui-focused": {
                      border: "1px solid rgba(0, 0, 0, 0.08)",
                      bgcolor: "Background.textfield",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                    },
                  },
                }}
              />
            </Box>
            {isPanelVisible && (
              <Divider sx={{ mt: 2, color: "1px solid rgba(0, 0, 0, 0.08)" }} />
            )}

            <Box
              sx={{
                position: "relative",
                minHeight: isPanelVisible && 64, // ensure height is preserved during transition
                overflow: "hidden",
                opacity: isPanelVisible ? 1 : 0,
                visibility: isPanelVisible ? "visible" : "hidden",
                maxHeight: isPanelVisible ? 400 : 0,
                transform: isPanelVisible
                  ? "translateY(0)"
                  : "translateY(-4px)",
                transition:
                  "opacity 0.2s ease, min-height 0.3s ease, max-height 0.3s ease, transform 0.2s ease, visibility 0.2s ease",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  opacity: isInitialLoading ? 1 : 0,
                  transition: "opacity 0.35s ease, padding 0.2s ease",
                  display: "flex",
                  justifyContent: "center",
                  py: isInitialLoading ? 2 : 0,
                  pointerEvents: isInitialLoading ? "auto" : "none",
                }}
              >
                <CircularProgress size={20} />
              </Box>

              <Box
                sx={{
                  opacity: showResults ? 1 : 0,
                  visibility: showResults ? "visible" : "hidden",
                  transition:
                    "opacity 0.2s ease, visibility 0.2s ease, padding 0.2s ease",
                  py: showResults ? 1.5 : 0,
                  maxHeight: 400,
                  overflowY: "auto",
                }}
              >
                {showResults && results.patients.length > 0
                  ? results.patients.slice(0, 50).map((patient, i) => (
                      <Box
                        key={patient.id}
                        ref={(el) => {
                          resultRefs.current[i] = el;
                        }}
                        component={RouterLink}
                        to={`/patients/${patient.id}`}
                        onClick={handleClose}
                        onMouseEnter={() => {
                          setHighlightedIndex(i);
                          setIsMouseOver(true);
                        }}
                        onMouseLeave={() => {
                          setIsMouseOver(false);
                        }}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          p: 1.5,
                          borderRadius: 2,
                          textDecoration: "none",
                          color: "inherit",
                          transition: "background 0.2s ease",
                          backgroundColor: (() => {
                            if (!isMouseOver && i === highlightedIndex) {
                              return "rgba(99, 102, 241, 0.08)"; // keyboard highlight
                            }
                            if (isMouseOver && i === highlightedIndex) {
                              return "rgba(99, 102, 241, 0.08)"; // hover highlight
                            }
                            return "transparent";
                          })(),
                          "&:hover": {
                            cursor: "pointer",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: "50%",
                            bgcolor: "primary.light",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: 600,
                            color: "primary.contrastText",
                            fontSize: "0.9rem",
                            textTransform: "uppercase",
                          }}
                        >
                          {getInitials(
                            generateHumanDisplayName(patient.name[0]),
                          )}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 500,
                              fontSize: "1rem",
                              color: "text.primary",
                            }}
                          >
                            {highlightMatch(
                              generateHumanDisplayName(patient.name[0]),
                              value,
                            )}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "text.secondary",
                              fontSize: "0.85rem",
                            }}
                          >
                            {patient.gender &&
                              `Gender: ${startCase(patient.gender)} • `}
                            {patient.birthDate &&
                              `DOB: ${formatBirthDate(patient.birthDate)}`}
                          </Typography>
                        </Box>
                        <Tooltip title="More options">
                          <IconButton
                            size="small"
                            aria-label={`Options for ${generateHumanDisplayName(patient.name[0])}`}
                            onClick={(e) => openRowMenu(e, patient)}
                            sx={{ color: "text.secondary" }}
                          >
                            <DotsVertical fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ))
                  : showResults && (
                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            color: "text.secondary",
                            letterSpacing: 0.5,
                            mb: 1,
                          }}
                        >
                          No patient results
                        </Typography>
                        <Button
                          onClick={createNewPatient}
                          variant="text"
                          disableRipple
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            p: 1,
                            width: "100%",
                            justifyContent: "flex-start",
                            textTransform: "none",
                            borderRadius: 2,
                            fontWeight: 500,
                            color: "text.primary",
                            "&:hover": {
                              backgroundColor: "#f5f5f5",
                            },
                          }}
                        >
                          <Box
                            sx={{
                              fontSize: "1.25rem",
                              color: "text.secondary",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            ＋
                          </Box>
                          <Box>
                            <Typography variant="body1">
                              Create new patient
                              {Object.values(patientName).some(Boolean) && (
                                <>
                                  {" "}
                                  <strong>
                                    “{startCase(patientName.firstName)}{" "}
                                    {startCase(patientName.family)}”
                                  </strong>
                                </>
                              )}
                            </Typography>
                          </Box>
                        </Button>
                      </Box>
                    )}
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 2,
                mt: isPanelVisible ? 0 : 2,
                px: 0.5,
                py: 1.25,
                borderTop: 1,
                borderColor: "divider",
              }}
            >
              <KeyboardLegendItem
                keys={
                  <>
                    <KeyHint>↑</KeyHint>
                    <KeyHint>↓</KeyHint>
                  </>
                }
                label="to navigate"
              />
              <KeyboardLegendItem
                keys={<KeyHint>↵</KeyHint>}
                label="to select"
              />
              <KeyboardLegendItem
                keys={<KeyHint>esc</KeyHint>}
                label="to close"
              />
            </Box>
          </Paper>
        </Box>
      </Modal>

      <Menu
        anchorEl={rowMenuAnchor}
        open={Boolean(rowMenuAnchor)}
        onClose={closeRowMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              mt: 0.5,
              minWidth: 260,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              boxShadow: `
                0px 4px 8px rgba(0, 0, 0, 0.04),
                0px 12px 24px rgba(0, 0, 0, 0.08)
              `,
              "& .MuiList-root": { py: 0.75 },
            },
          },
        }}
      >
        {rowMenuPatient && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              px: 1.5,
              pt: 0.5,
              pb: 1.25,
            }}
          >
            <Box
              sx={{
                flexShrink: 0,
                width: 32,
                height: 32,
                borderRadius: "50%",
                bgcolor: "primary.light",
                color: "primary.contrastText",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "0.75rem",
                fontWeight: 600,
              }}
            >
              {getInitials(generateHumanDisplayName(rowMenuPatient.name[0]))}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2" noWrap sx={{ fontWeight: 600 }}>
                {generateHumanDisplayName(rowMenuPatient.name[0])}
              </Typography>
              {rowMenuPatient.patientId && (
                <Typography
                  variant="caption"
                  noWrap
                  sx={{ display: "block", color: "text.secondary" }}
                >
                  MR# {rowMenuPatient.patientId}
                </Typography>
              )}
              {rowMenuTelecom.primary && (
                <Typography
                  variant="caption"
                  noWrap
                  sx={{ display: "block", color: "text.secondary" }}
                >
                  {rowMenuTelecom.primary}
                  {rowMenuTelecom.extraCount > 0 &&
                    ` (+ ${rowMenuTelecom.extraCount} more)`}
                </Typography>
              )}
            </Box>
          </Box>
        )}

        <Divider sx={{ mb: 0.75 }} />

        {[
          {
            key: "view",
            label: "View profile",
            icon: <PersonOutline fontSize="small" />,
            onClick: () => goToPatient(`/patients/${rowMenuPatient?.id}`),
          },
          {
            key: "share",
            label: "Share profile",
            icon: <Share fontSize="small" />,
            onClick: () => sharePatient(rowMenuPatient?.id),
          },
          {
            key: "edit",
            label: "Edit patient",
            icon: <Edit fontSize="small" />,
            onClick: () => goToPatient(`/patients/${rowMenuPatient?.id}/edit`),
          },
        ].map(({ key, label, icon, onClick }) => (
          <MenuItem
            key={key}
            onClick={onClick}
            sx={{
              mx: 0.75,
              px: 1,
              py: 0.75,
              borderRadius: 1.5,
              minHeight: "auto",
              "&:hover": { bgcolor: "action.hover" },
            }}
          >
            <ListItemIcon
              sx={{ minWidth: 32, marginRight: 0, color: "text.secondary" }}
            >
              {icon}
            </ListItemIcon>
            <ListItemText
              primaryTypographyProps={{
                variant: "body2",
                fontWeight: 500,
              }}
            >
              {label}
            </ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default ContentSearch;
